import React from 'react';
import { Box, Paper, Chip, Accordion, AccordionSummary, AccordionDetails, Typography } from '@mui/material';
import { blue, grey, blueGrey } from '@mui/material/colors';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useChat } from '../../context/chatContext';

import { ChatMessage as ChatMessageType, ThinkingStep, FileAttachment, ThoughtStep } from '../../types';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';

// ====================================================================================
// 1. New Component: MessageThoughts.tsx
// This component renders the expandable "thought process" section.
// ====================================================================================

/**
 * Generates a readable title for a given agent name.
 * @param {string} agent - The agent name (e.g., 'TiAgent').
 * @returns {string} A user-friendly title.
 */
const generateAgentTitle = (agent: string): string => {
    const agentMap: { [key: string]: string } = {
        'Fi': 'Subjective Feeling Agent',
        'Ti': 'Subjective Thinking Agent',
        'Te': 'Objective Thinking Agent',
        'Fe': 'Objective Feeling Agent',
        'Ne': 'Objective Intuition Agent',
        'Ni': 'Subjective Intuition Agent',
    };
    const key = agent.slice(0, 2);
    return agentMap[key] || agent; // Fallback to the original agent name
};

/**
 * Renders a single step in the thought process.
 */
const ThoughtStepItem: React.FC<{ step: ThoughtStep; }> = ({ step }) => (
  <Accordion
    disableGutters
    elevation={0}
    sx={{
      backgroundColor: 'transparent',
      '&:before': { display: 'none' },
      boxShadow: 'none',
      border: 'none',
    }}
  >
    <AccordionSummary
      // expandIcon={<ExpandMoreIcon />}
      aria-controls={`thought-${step.timestamp}-content`}
      id={`thought-${step.timestamp}-header`}
      sx={{
        minHeight: 36,
        '& .MuiAccordionSummary-content': { my: 0 },
        position: 'sticky',       // <-- make the entire header sticky
        top: '-10px',                   // <-- stick to top of the scroll area
        zIndex: 2,
        bgcolor: '#242424',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Typography variant="subtitle2">{generateAgentTitle(step.agent)}</Typography>
    </AccordionSummary>

    <AccordionDetails sx={{ pt: 2 }}>
      <Box sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'primary.main' }}>
        <Typography
          variant="caption"
          display="block"
          sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: 'text.secondary' }}
        >
          <Markdown remarkPlugins={[remarkGfm, remarkBreaks]}>{step.text || ''}</Markdown>
        </Typography>
      </Box>
    </AccordionDetails>
  </Accordion>
);


interface MessageThoughtsProps {
  steps: ThoughtStep[];
}

/**
 * An expandable accordion component to display the thought process within a chat message.
 */
const MessageThoughts: React.FC<MessageThoughtsProps> = ({ steps }) => {
  const hideAgents = ['SeAgent']; // Agents to hide from the display
  const visibleSteps = React.useMemo(
    () => steps.filter(s => !hideAgents.includes(s.agent)),
    [steps]
  );

  if (visibleSteps.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
      <Accordion
        disableGutters
        elevation={0}
        sx={{
          backgroundColor: 'transparent',
          '&:before': { display: 'none' }, // Removes default accordion top border
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{color: 'text.secondary'}} />}
          aria-controls="message-thoughts-content"
          id="message-thoughts-header"
          sx={{
            minHeight: 'auto',
            p: 0,
            '&.Mui-expanded': {
                minHeight: 'auto',
            },
            '& .MuiAccordionSummary-content': {
                my: 0,
            },
          }}
        >
             <Typography variant="body2" color="text.secondary">
            Show thought process
          </Typography>
        </AccordionSummary>
          
        <AccordionDetails sx={{ p: 0, pt: 1, maxHeight: 350, overflowY: 'auto' }}>
            {visibleSteps.map((step, index) => (
                <ThoughtStepItem key={index} step={step} />
            ))}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default MessageThoughts